import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useCompany, useSession } from '../App';
import { signOutNow } from '../lib/firebase';
import { api } from '../lib/api';
import { companyBase } from '../lib/paths';
import { roleAtLeast } from '../lib/roles';
import { Badge } from './ui';

/**
 * App shell: a persistent left rail and a thin top bar.
 *
 * Nav order follows the order a person actually works in — the register first,
 * then the things that change it, then modelling, then records. Administrative
 * surfaces sit below a divider so they never compete with daily work.
 */
/**
 * Nav targets are path *segments*, resolved to absolute URLs below.
 *
 * They must not be relative `to` values. These links live in a descendant
 * <Routes> mounted under `/companies/:companyId/*`, and a relative target inside
 * a splat route resolves against the matched pathname *including* the splat — so
 * the first click worked and every click after it appended:
 *
 *   /companies/x            + "stakeholders" -> /companies/x/stakeholders   (fine)
 *   /companies/x/stakeholders + "waterfall"  -> /companies/x/stakeholders/waterfall
 *
 * The second URL matches no route, falls through to `path="*"`, and renders
 * nothing — a blank view on every tab after the first, which cleared only by
 * leaving the company and coming back (resetting the URL to /companies/x). That
 * is exactly how it was reported: "always the 2nd click".
 *
 * Absolute hrefs make the accumulation impossible rather than merely unlikely.
 */
const NAV = [
  { segment: '', label: 'Overview', end: true, icon: '◱' },
  // Both read the full register with no self-scoping (unlike Stakeholders/
  // Securities/Documents below, which scope themselves for EMPLOYEE/INVESTOR).
  // EMPLOYEE/INVESTOR use the Overview's own-holdings view instead.
  { segment: 'cap-table', label: 'Cap table', icon: '▤', minRole: 'AUDITOR' },
  { segment: 'stakeholders', label: 'Stakeholders', icon: '◉' },
  { segment: 'securities', label: 'Securities', icon: '◈' },
  { segment: 'rounds', label: 'Rounds', icon: '↗' },
  { segment: 'waterfall', label: 'Waterfall', icon: '⇲', minRole: 'AUDITOR' },
  { divider: true },
  // The AI surfaces. Review is gated at LEGAL because approving a proposal performs
  // the underlying mutation, and the assistant at ADMIN because it can read every
  // holder's position and queue changes across every entity.
  { segment: 'review', label: 'Review', icon: '✓', minRole: 'LEGAL' },
  { segment: 'assistant', label: 'Assistant', icon: '✦', minRole: 'ADMIN' },
  { divider: true },
  { segment: 'structure', label: 'Classes & plans', icon: '⌗' },
  { segment: 'valuations', label: 'Valuations', icon: '◐' },
  { segment: 'documents', label: 'Documents', icon: '▭' },
  { segment: 'members', label: 'Access', icon: '⚿', minRole: 'LEGAL' },
  { segment: 'audit', label: 'Audit log', icon: '≡', minRole: 'AUDITOR' },
  // Entity details (legal name, jurisdiction, tax ID...), not cap table data
  // — ADMIN, matching PATCH /companies/:id's own gate.
  { segment: 'settings', label: 'Settings', icon: '⚙', minRole: 'ADMIN' },
];

/** The single source of truth for which role a route needs -- consumed
 * here to hide the nav link, and by `RequireRole` in App.jsx to actually
 * enforce it. Hiding the link alone doesn't stop a bookmarked or shared
 * URL from reaching the page directly. */
export const ROUTE_MIN_ROLE = Object.fromEntries(
  NAV.filter((item) => item.minRole).map((item) => [item.segment, item.minRole])
);

export default function Layout({ children }) {
  const { company, companyId, role } = useCompany();
  const { profile } = useSession();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme || 'system');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (theme === 'system') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = theme;
  }, [theme]);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <Link to="/" className="brand-mark" title="All companies">
            <span className="brand-dot" aria-hidden="true" />
            Sevah
          </Link>
        </div>

        <button className="company-switch" onClick={() => navigate('/')}>
          <span className="company-name">{company.name}</span>
          <span className="company-meta">
            {company.entity_type?.replace(/_/g, '-')} · switch
          </span>
        </button>

        <nav className="nav">
          {NAV.map((item, index) =>
            item.divider ? (
              <hr key={`div-${index}`} className="nav-divider" />
            ) : item.minRole && !roleAtLeast(role, item.minRole) ? null : (
              <NavLink
                key={item.segment || 'overview'}
                to={companyBase(companyId, item.segment)}
                end={item.end}
                className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}`}
              >
                <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="sidebar-foot">
          <Badge tone="info">{String(role ?? '').toLowerCase()}</Badge>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="topbar-title">{company.name}</div>
          <div className="topbar-right">
            <select
              className="theme-select"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              aria-label="Colour theme"
            >
              <option value="system">System theme</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>

            <div className="user-menu">
              <button className="avatar" onClick={() => setMenuOpen((o) => !o)} aria-haspopup="menu">
                {(profile?.user?.displayName || profile?.user?.email || '?').slice(0, 1).toUpperCase()}
              </button>
              {menuOpen ? (
                <div className="menu" role="menu" onMouseLeave={() => setMenuOpen(false)}>
                  <div className="menu-head">
                    <div>{profile?.user?.displayName || 'Signed in'}</div>
                    <div className="muted">{profile?.user?.email}</div>
                  </div>
                  <button
                    role="menuitem"
                    onClick={async () => {
                      // Denylist the token server-side, then drop the local session.
                      await api.signOutServer().catch(() => {});
                      await signOutNow();
                    }}
                  >
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main className="content" key={companyId}>
          {children}
        </main>
      </div>
    </div>
  );
}
